import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { fbt } from 'fbtee'
import { ChevronRight } from 'lucide-react'
import { recordTrace, useTraceStart } from '../lib/perf'
import { isFestivalInvite, festivalInviteEventId } from '../lib/inviteEntryPoint'
import { pageTitle, useDocumentTitle } from '../hooks/useDocumentTitle'
import { useAuth } from '../hooks/useAuth'
import { useGuestLandingSession } from '../hooks/useGuestLandingSession'
import { useFriendships, findUserByFriendCode } from '../hooks/useFriendships'
import { useFriendEventInterests } from '../hooks/useFriendEventInterests'
import { useEventsByIds } from '../hooks/useEventsByIds'
import { byStartDateAsc } from '../lib/utils'
import { isEventActive } from '../lib/formatDate'
import { getNow } from '../lib/clock'
import { track } from '../lib/analytics'
import { useTransitionNavigate } from '../lib/viewTransition'
import { UserAvatar } from '../components/UserAvatar'
import { Button } from '../components/ui/button'
import { friendInvitePrompt } from '../lib/guestPrompts'
import { useGuestGuard } from '../hooks/useGuestGuard'
import type { UserProfile } from '../types'
import { backToHomeButtonLabel } from '../lib/commonStrings'
import { LinkLandingMessage, LinkLandingShell, LinkLandingSpinner } from '../components/LinkLandingState'

type Status = 'auth' | 'resolving' | 'invalid' | 'self' | 'confirm'

export default function AddFriendPage() {
  useDocumentTitle(pageTitle(fbt('Add friend', 'Browser tab title for the friend invite page')))
  const { friendCode } = useParams<{ friendCode: string }>()
  const location = useLocation()
  // Pinned at mount — see JoinPage.
  const [fromFestival] = useState(() => isFestivalInvite(location.search))
  // A festival invite lands the new friend back in that festival, not the
  // friends list away from it.
  const [festivalEventId] = useState(() => festivalInviteEventId(location.search))
  const friendDest = festivalEventId ? `/live-festival/${festivalEventId}` : '/friends'
  const { user, signedInUid } = useAuth()
  const navigate = useTransitionNavigate()
  const isGuest = !signedInUid

  const [status, setStatus] = useState<Status>('auth')
  const [inviter, setInviter] = useState<UserProfile | null>(null)
  const [openedTracked, setOpenedTracked] = useState(false)
  const traceStart = useTraceStart()

  const { friendIds, incoming, acceptRequest, acceptInvite } = useFriendships(signedInUid)

  useGuestLandingSession(fromFestival ? 'qr' : 'friend_invite')

  // Flip to 'resolving' synchronously during render once we have a session +
  // code, so the resolve effect never calls setState synchronously.
  const lookupKey = user && friendCode ? `${user.uid}|${friendCode}` : null
  const [resolvedKey, setResolvedKey] = useState<string | null>(null)
  if (lookupKey && lookupKey !== resolvedKey) {
    setResolvedKey(lookupKey)
    setStatus('resolving')
  }

  // Resolve the friend code → inviter profile.
  useEffect(() => {
    if (!user || !friendCode) return
    let cancelled = false
    findUserByFriendCode(friendCode)
      .then(found => {
        if (cancelled) return
        if (!openedTracked) {
          setOpenedTracked(true)
          track('friend_invite_opened', {})
        }
        if (!found) {
          setInviter(null)
          setStatus('invalid')
          recordTrace('add_friend_page_to_content', traceStart.wall, performance.now() - traceStart.perf)
          return
        }
        setInviter(found)
        setStatus(found.uid === user.uid ? 'self' : 'confirm')
        recordTrace('add_friend_page_to_content', traceStart.wall, performance.now() - traceStart.perf)
      })
      .catch(() => {
        if (!cancelled) setStatus('invalid')
      })
    return () => {
      cancelled = true
    }
  }, [user, friendCode, openedTracked, traceStart])

  // Already friends → straight to the friends list.
  useEffect(() => {
    if (isGuest || !inviter) return
    if (friendIds.includes(inviter.uid)) navigate(friendDest, { replace: true })
  }, [isGuest, inviter, friendIds, navigate, friendDest])

  const hasIncomingFromInviter = useMemo(
    () => !!inviter && incoming.some(f => f.userIds.includes(inviter.uid)),
    [incoming, inviter],
  )

  const handleAdd = useCallback(async () => {
    if (!inviter) return
    if (hasIncomingFromInviter) await acceptRequest(inviter.uid)
    else await acceptInvite(inviter.uid, fromFestival ? 'festival_qr' : 'invite_link')
    toast.success(
      fbt(
        'Added ' + fbt.param('name', inviter.displayName?.trim() || fbt('A Faro user', 'Fallback inviter name on the friend invite page')),
        'Toast after adding a friend via invite link',
      ),
    )
    navigate(friendDest, { replace: true })
  }, [inviter, hasIncomingFromInviter, acceptRequest, acceptInvite, navigate, fromFestival, friendDest])

  if (status === 'invalid') {
    return (
      <LinkLandingMessage
        title={
          <fbt desc="Heading when a friend invite link is not valid">This invite link isn't valid</fbt>
        }
        body={
          <fbt desc="Body when a friend invite link is invalid">
            Double-check the link, or ask your friend for a fresh one.
          </fbt>
        }
        action={
          <Button variant="outline" size="sm" onClick={() => navigate('/', { replace: true })}>
            {backToHomeButtonLabel()}
          </Button>
        }
      />
    )
  }

  if (status === 'self') {
    return (
      <LinkLandingMessage
        title={
          <fbt desc="Heading when the user opens their own friend invite link">This is your invite link</fbt>
        }
        body={
          <fbt desc="Body when the user opens their own friend invite link">
            Share it with friends so they can add you.
          </fbt>
        }
        action={
          <Button variant="outline" size="sm" onClick={() => navigate('/friends')}>
            <fbt desc="Button to go to the friends page from the own-link state">Go to friends</fbt>
          </Button>
        }
      />
    )
  }

  if (status === 'confirm' && inviter) {
    return <ConfirmView inviter={inviter} isGuest={isGuest} onAdd={handleAdd} friendCode={friendCode!} />
  }

  return <LinkLandingSpinner label={fbt('Looking up invite…', 'Loading message while resolving a friend invite link')} />
}

function ConfirmView({
  inviter,
  isGuest,
  onAdd,
  friendCode,
}: {
  inviter: UserProfile
  isGuest: boolean
  onAdd: () => void
  friendCode: string
}) {
  const navigate = useTransitionNavigate()
  const { prompt: openGuard } = useGuestGuard()
  const name = inviter.displayName?.trim() || fbt('A Faro user', 'Fallback inviter name on the friend invite page')
  const { map: interests } = useFriendEventInterests([inviter.uid])
  const inviterEventIds = useMemo(() => [...interests.keys()], [interests])
  const { events } = useEventsByIds(inviterEventIds)

  const now = getNow()
  const upcoming = events
    .filter(e => {
      const b = interests.get(e.id)
      const has = b?.going.includes(inviter.uid) || b?.interested.includes(inviter.uid)
      return has && isEventActive(e, now)
    })
    .sort(byStartDateAsc)
    .map(e => ({
      event: e,
      going: !!interests.get(e.id)?.going.includes(inviter.uid),
    }))

  function handleClick() {
    if (isGuest) {
      const ctx = friendInvitePrompt(name)
      openGuard({ ...ctx, afterSignIn: { pathname: `/add/${friendCode}` } })
      return
    }
    onAdd()
  }

  return (
    <LinkLandingShell>
      <UserAvatar profile={inviter} size="lg" />
      <h1 className="text-xl font-semibold tracking-tight">
        {fbt(
          fbt.param('name', name) + ' wants to be friends',
          'Heading on the friend invite landing showing the inviter name',
        )}
      </h1>

      {upcoming.length > 0 && (
        <div className="w-full space-y-2 text-left">
          <p className="text-sm text-muted-foreground text-center">
            {fbt(
              'Tap a festival to see ' + fbt.param('name', name) + "'s picks",
              'Hint above the list of the inviter’s festivals on the friend invite page',
            )}
          </p>
          <ul className="flex flex-col gap-2">
            {upcoming.map(({ event, going }) => (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/events/${event.id}?friend=${inviter.uid}`)}
                  className="w-full flex items-center justify-between gap-3 rounded-xl bg-card hover:bg-muted/60 ring-1 ring-foreground/10 px-4 py-3 cursor-pointer transition-colors"
                >
                  <span className="text-sm font-medium truncate">{event.name}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {going
                        ? fbt('Going', 'Pill marking a festival the inviter is going to')
                        : fbt('Interested', 'Pill marking a festival the inviter is interested in')}
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button onClick={handleClick} className="cursor-pointer">
        {fbt('Add ' + fbt.param('name', name), 'Button to add the inviter as a friend')}
      </Button>
    </LinkLandingShell>
  )
}


